import {
  type PreorderStatus,
  type Product,
} from "@/lib/products";
import { type HomeCatalogItem } from "@/lib/homeCatalog";
import { normalizeBrandIconUrl, normalizeBrandTelegramUrl } from "@/lib/brands";
import type { MetaCheckoutRefs } from "@/lib/metaCheckout";
import { toCardProducts } from "@/lib/productCard";
import {
  deriveComboCount,
  parseBlockedComboList,
  shouldLazyLoadVariants,
  VARIANT_FETCH_CAP,
} from "@/lib/lazyVariants";
import { storefrontComboSource } from "@/lib/comboListMode";
import {
  mapPublicProduct,
  mapPublicVariant,
  sortProductImages,
  type PublicProductRow,
} from "@/lib/publicProductMapping";
import { pickProductCoverUrl } from "@/lib/mediaUrl";
import { supabase } from "@/lib/supabase/client";
import {
  fetchAllProductVariants,
  hydrateCappedVariants,
  PGREST_MAX_ROWS,
} from "@/lib/supabase/fetchAllVariants";
import { withTimeout } from "@/lib/withTimeout";
import { inheritedPricingFromRows, type InheritedPricingRpcRow } from "@/lib/inheritedPricingRow";

// `supabase-js` (fetch) tak punya timeout default. Halaman detail produk
// di-render sisi-server; bila koneksi ke Supabase menggantung, request SSR ikut
// menggantung sampai reverse-proxy menyerah. Batasi tiap query publik agar
// gagal cepat → fallback (null/[]) ketimbang menggantung.
const PUBLIC_FETCH_TIMEOUT_MS = 8000;

// Timeout khusus (lebih longgar) untuk MENGAMBIL ULANG semua varian produk
// >1000 kombinasi. Fetch ini terpaginasi-paralel (beberapa request), jadi butuh
// ruang lebih dari query tunggal — terutama produk sangat besar (mendekati
// 10.000 varian). Bila timeout, fallback = 1000 varian pertama → sebagian
// kombinasi TAK BISA dipesan (persis bug yang diperbaiki), jadi di sini
// KELENGKAPAN varian lebih penting daripada latensi. Aman karena data produk
// di-cache ~60 dtk (getCachedProductBySlug) → jalur ini cuma jalan saat
// cache-miss, bukan tiap request.
const VARIANT_REFETCH_TIMEOUT_MS = 20000;

// Kolom varian yang BENAR-BENAR dipakai storefront (mapPublicProduct). SENGAJA
// bukan `*`: tabel product_variants punya ~24 kolom, ~11 di antaranya internal
// (hpp, qty_ordered/qty_arrived, production_status, submitted_*, arrival_status,
// is_active, created_at, product_id) yang TAK PERNAH dibaca storefront. Menariknya
// via `*` membuang bandwidth & memori server — kritis untuk produk ribuan varian
// (mis. ~10.000) yang diambil terpaginasi. Satu sumber daftar kolom ini dipakai
// di select detail, listing beranda, DAN re-fetch >1000 varian agar selalu sinkron.
// PENTING: bila menambah field varian baru ke UI storefront, tambahkan kolomnya
// di sini juga — kalau tidak, field itu akan undefined.
const PUBLIC_VARIANT_COLUMNS =
  "id, name, sku, option_ids, price_normal, price_extra, stock, po_quota, weight, diskon_mode, diskon_reseller, diskon_agen, diskon_slot3, diskon_slot4, diskon_slot5, diskon_slot6, diskon_slot7, diskon_slot8, diskon_slot9, diskon_slot10, diskon_slot11, diskon_slot12, diskon_slot13, diskon_slot14, diskon_slot15, sort_order";

// Select untuk halaman detail produk: butuh variant_groups + opsi (picker varian)
// dan seluruh media. Varian pakai kolom ramping (PUBLIC_VARIANT_COLUMNS).
// Kolom PRODUK juga eksplisit (cermin PublicProductRow) — dulu `*` ikut membawa
// kolom internal berat yang tak pernah dibaca storefront (`search_text`
// denormalisasi pencarian mig 159, `sizes` array mig 197, dsb.) pada query
// bervolume tertinggi se-aplikasi (tiap kunjungan halaman produk; audit
// performa 2026-07). PENTING: bila mapper butuh kolom produk baru, tambahkan
// di sini juga — kalau tidak, field itu akan undefined. AWAS: kolom yang
// TIDAK ADA di tabel membuat PostgREST menolak SELURUH query (400) →
// getPublicProductBySlug null → SEMUA halaman produk 404. `price_reseller`/
// `price_agen` di PublicProductRow TIDAK punya kolom nyata di `products`
// (warisan tipe lama; dengan `*` dulu selalu undefined) — jangan diminta.
const PRODUCT_DETAIL_PRODUCT_COLUMNS = [
  "id",
  "slug",
  "name",
  "brand",
  "category",
  "segment",
  "description",
  "preorder_deadline",
  "estimated_ready",
  "status",
  "product_type",
  "po_limited",
  "price_normal",
  "reseller_discount",
  "agent_discount",
  "dp_required",
  "dp_amount",
  "dp_mode",
  // mig 6840 — "Perkiraan Rp X" untuk PO harga menyusul (grant kolom anon di sana).
  "price_estimate",
  // B1 daftar kombinasi tertutup (migrasi varian_daftar_tertutup, grant kolom anon di sana).
  "absent_combos_sellable"
].join(", ");

const PRODUCT_DETAIL_SELECT = `${PRODUCT_DETAIL_PRODUCT_COLUMNS}, product_media(media_type, media_url, storage_path, is_cover, sort_order, is_size_guide), product_variants(${PUBLIC_VARIANT_COLUMNS}), variant_groups(id, name, sort_order, use_as_image_primary, variant_options(id, name, image_url, sort_order))`;

// Select RINGAN untuk beranda (listing). Kartu produk hanya butuh gambar cover +
// kolom HARGA per-varian (getProductPrice). Kita SENGAJA tidak mengambil
// variant_groups/variant_options (picker varian — tak dipakai di kartu) maupun
// kolom berat `description`: keduanya membengkak drastis saat produk banyak dan
// memperlambat transfer + parse. Halaman detail tetap memakai select penuh.
const PRODUCT_LIST_SELECT = [
  "id",
  "slug",
  "name",
  "brand",
  "category",
  "segment",
  "preorder_deadline",
  "estimated_ready",
  "status",
  "product_type",
  "price_normal",
  "reseller_discount",
  "agent_discount",
  "dp_required",
  "dp_amount",
  "dp_mode",
  "product_media(media_type, media_url, storage_path, is_cover, sort_order)",
  `product_variants(${PUBLIC_VARIANT_COLUMNS})`
].join(", ");

// Kolom kecil untuk "index katalog" beranda — cukup untuk semua logika
// discovery (filter/rail/tile/hitung tab) tanpa media/varian yang berat.
// PF-01C (audit 2026-08): `name` DIPANGKAS — tak ada pembaca nyata (feed
// beranda selalu `query: ""`, pencarian hidup di `/cari` via RPC; sitemap
// hanya butuh slug). AWAS mig 3550: menambah kolom kembali ke daftar ini
// menuntut grant kolom anon + `public_anon_column_lint()` — tanpa itu
// PostgREST menolak SELURUH query (400) dan beranda kosong senyap.
const CATALOG_INDEX_SELECT =
  "id, slug, brand, category, segment, product_type, status, preorder_deadline, updated_at";

type CatalogIndexRow = {
  id: string;
  slug: string;
  brand: string | null;
  category: string | null;
  segment: string | null;
  product_type?: "preorder" | "ready_stock" | null;
  status: PreorderStatus;
  preorder_deadline: string | null;
  /** Untuk `lastmod` sitemap — lihat catatan di `HomeCatalogItem.updatedAt`. */
  updated_at?: string | null;
};

function mapCatalogIndexItem(row: CatalogIndexRow): HomeCatalogItem {
  return {
    id: row.id,
    slug: row.slug,
    brand: row.brand ?? "",
    category: row.category ?? undefined,
    segment: row.segment ?? undefined,
    productType: row.product_type ?? "preorder",
    status: row.status,
    deadline: row.preorder_deadline ?? "",
    updatedAt: row.updated_at ?? undefined,
  };
}

/**
 * Index RINGAN semua produk discoverable (ready stock + preorder OPEN PO),
 * urut terbaru. Satu query kolom kecil — payload tak lagi membawa media/varian
 * untuk SETIAP produk. Filter `or(...)` cermin `isDiscoverableProduct`.
 */
export async function getDiscoverableCatalogIndex(
  storeId?: string | null
): Promise<HomeCatalogItem[]> {
  if (!supabase) {
    return [];
  }

  let q = supabase
    .from("products")
    .select(CATALOG_INDEX_SELECT)
    .eq("is_active", true)
    .or(
      "product_type.eq.ready_stock,and(product_type.eq.preorder,status.eq.OPEN PO)"
    );
  // MULTI-TENANT FASE 4: katalog per-domain — storeId dari resolusi Host
  // (kosong/null = perilaku lama lintas-toko, hanya utk jalur tanpa konteks).
  if (storeId) q = q.eq("store_id", storeId);
  // `id` = pemutus seri: produk yang lahir dalam satu transaksi berbagi
  // `created_at`, dan tanpa pemutus urutannya bebas berganti tiap refresh
  // cache — feed geser halaman produk (productFeed.ts) lalu bisa melompati
  // atau mengulang produk. Pola yang sama dengan urutan varian kartu.
  const { data, error } = await q
    .order("created_at", { ascending: false })
    .order("id", { ascending: true });

  if (error || !data?.length) {
    return [];
  }

  return (data as unknown as CatalogIndexRow[]).map(mapCatalogIndexItem);
}

/**
 * Hidrasi kartu: data PENUH (gambar + varian harga) untuk sekumpulan id yang
 * terlihat saja. Dipanggil server (paint awal) maupun klien (load-more/filter/
 * search). PostgREST `.in()` tak menjaga urutan → urut ulang sesuai `ids`.
 */
export async function getPublicProductsByIds(
  ids: string[],
  storeId?: string | null
): Promise<Product[]> {
  if (!supabase || !ids.length) {
    return [];
  }

  let q = supabase
    .from("products")
    .select(PRODUCT_LIST_SELECT)
    .eq("is_active", true)
    .in("id", ids);
  // FASE 4: id sudah datang dari sumber ter-scope (RPC/index) — filter ini
  // defense-in-depth agar id tempaan lintas-toko tak terhidrasi.
  if (storeId) q = q.eq("store_id", storeId);
  const { data, error } = await q;

  if (error || !data?.length) {
    return [];
  }

  const rows = data as unknown as PublicProductRow[];
  // Kartu produk >1000 varian: harga "mulai dari" (getProductPrice) bisa salah
  // karena embed varian dipotong max-rows. Ambil ulang penuh HANYA utk produk
  // yang kena cap (kasus umum: nol query tambahan). Fail-open bila gagal —
  // pakai data apa adanya ketimbang menggagalkan render kartu.
  try {
    await hydrateCappedVariants(
      supabase,
      rows as unknown as Record<string, unknown>[],
      PUBLIC_VARIANT_COLUMNS
    );
  } catch {
    /* biarkan truncated */
  }

  const mapped = rows.map(mapPublicProduct);
  const orderById = new Map(ids.map((id, index) => [id, index]));
  return mapped.sort(
    (a, b) =>
      (orderById.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (orderById.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  );
}

/**
 * Hidrasi kartu untuk DITAMPILKAN — varian dipangkas ke varian perwakilan
 * DI SISI SQL (audit 2026-09-02).
 *
 * Kartu hanya membaca SATU varian: `representativeVariant()` di `pricing.ts`
 * = varian PERTAMA yang ber-`price_normal` menurut urutan `sort_order`
 * (`mapPublicProduct` mengurutkannya). Versi lama mengambil SELURUH varian
 * (ditambah `hydrateCappedVariants` untuk produk >1.000 varian) lalu
 * memangkasnya di klien lewat `toCardProducts` — di PROD itu 2.864 query
 * anon/hari dan ±650 MB JSON/hari yang diunduh HP reseller hanya untuk
 * dibuang; tiga dari empat produk pemicunya bahkan TIDAK punya varian
 * berharga (hasil akhirnya array kosong → fallback `products.price_normal`).
 *
 * Sekarang embed-nya disaring, diurut, dan dibatasi oleh PostgREST per produk
 * (LATERAL … LIMIT 1):
 *   • `product_variants.price_normal=not.is.null` — hanya varian berharga;
 *   • `product_variants.order=sort_order.asc,id.asc` — urutan yang sama
 *     dengan `mapPublicProduct`, `id` sebagai pemutus seri agar deterministik;
 *   • `product_variants.limit=1` — varian perwakilan itu saja.
 * Hasil untuk kartu IDENTIK dengan jalur lama: varian pertama ber-harga, atau
 * array kosong bila tak ada (fallback `products.price_normal` tetap jalan).
 * Diverifikasi langsung ke PostgREST PROD 2026-09-03 untuk RONA NIRMALA
 * (2.470 varian, nol berharga → `[]`) dan NAFAS PERTAMA BATCH 2 ET (4.644
 * varian, 22 berharga → tepat varian yang dipilih `order by sort_order, id`).
 *
 * ⛔ JANGAN diubah menjadi `product_variants!inner(...)`: `!inner` membuang
 * PRODUK yang belum punya varian berharga dari daftar — produk hilang dari
 * beranda. Embed biasa hanya menyaring variannya. Dijaga tripwire
 * `publicDataCardQuery.test.ts`.
 *
 * Pemesanan TIDAK pernah terjadi dari kartu (satu-satunya `addToCart` ada di
 * `ProductDetail`, yang memuat lewat `getPublicProductBySlug` dengan jalur
 * variannya sendiri), jadi memangkas varian di sini tidak bisa membuat
 * kombinasi apa pun mustahil dipesan — lihat `src/lib/productCard.ts`.
 *
 * Pemakai: beranda, /kategori, /merek, rail, promo, PO segera ditutup,
 * katalog reseller. BUKAN /cari — `SearchClient` tetap memakai
 * `getPublicProductsByIds` (varian penuh untuk `matchedVariantLabel`).
 */
export async function getPublicProductCardsByIds(
  ids: string[],
  storeId?: string | null
): Promise<Product[]> {
  if (!supabase || !ids.length) {
    return [];
  }

  let q = supabase
    .from("products")
    .select(PRODUCT_LIST_SELECT)
    .eq("is_active", true)
    .in("id", ids)
    .not("product_variants.price_normal", "is", null)
    .order("sort_order", { foreignTable: "product_variants", ascending: true })
    .order("id", { foreignTable: "product_variants", ascending: true })
    .limit(1, { foreignTable: "product_variants" });
  // FASE 4: id sudah datang dari sumber ter-scope (RPC/index) — filter ini
  // defense-in-depth agar id tempaan lintas-toko tak terhidrasi (cermin
  // `getPublicProductsByIds`).
  if (storeId) q = q.eq("store_id", storeId);
  const { data, error } = await q;

  if (error || !data?.length) {
    return [];
  }

  const rows = data as unknown as PublicProductRow[];
  const orderById = new Map(ids.map((id, index) => [id, index]));
  // `toCardProducts` tetap dipakai: mengosongkan `variantGroups` dan menjaga
  // kontrak "maksimal satu varian" walau bentuk embed suatu saat berubah.
  return toCardProducts(rows.map(mapPublicProduct)).sort(
    (a, b) =>
      (orderById.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (orderById.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  );
}

/**
 * Foto SAMPUL sekumpulan produk (`id → URL`) — bahan tile kategori dari foto
 * produk (`categoryCoverPhotos`). Cuma `product_media`, tanpa varian: jauh
 * lebih ringan dari hidrasi kartu. Fotonya SAMA dengan foto pertama kartu
 * produk (urutan `sortProductImages`, URL dari `resolveProductMediaUrl`).
 *
 * `storeId` WAJIB (aturan A2): toko tak ter-resolusi = kosong, bukan foto
 * lintas toko. Produk tanpa foto tidak masuk hasil.
 *
 * `null` = query GAGAL, beda dari `{}` = memang tak ada foto — pemanggil
 * ber-cache melempar pada `null` supaya kegagalan sesaat tak terkunci di
 * cache selama satu TTL.
 */
export async function getPublicProductCoverUrls(
  ids: string[],
  storeId: string | null
): Promise<Record<string, string> | null> {
  if (!supabase || !storeId || !ids.length) {
    return {};
  }

  const { data, error } = await supabase
    .from("products")
    .select("id, product_media(media_type, media_url, storage_path, is_cover, sort_order)")
    .eq("is_active", true)
    .eq("store_id", storeId)
    .in("id", ids);

  if (error) {
    return null;
  }

  const covers = new Map<string, string>();
  for (const row of data ?? []) {
    const media = [...(row.product_media ?? [])].sort(sortProductImages);
    const url = pickProductCoverUrl(media);
    if (url) covers.set(row.id, url);
  }
  return Object.fromEntries(covers);
}

/**
 * Kombinasi yang DIMATIKAN admin (`is_active = false`) sebuah produk, sebagai
 * daftar `option_ids` — lihat blok "Kombinasi yang DIMATIKAN admin" di
 * `lazyVariants.ts` untuk alasannya. Dipakai halaman produk (mode malas) DAN
 * Edit Pesanan pembeli (`EditOrderModal`): keduanya memilih per option id dan
 * sama-sama tak bisa melihat baris mati.
 *
 * Lewat RPC dan bukan `select` biasa karena policy RLS pembeli menuntut
 * `is_active = true`: baris yang dimatikan memang TIDAK BOLEH terbaca lewat
 * tabelnya. Yang dipulangkan RPC hanya `option_ids` — bukan nama, harga, atau
 * stok — jadi tak ada yang bocor selain "kombinasi ini tidak dijual", yang
 * justru harus dilihat pembeli.
 *
 * `preorder_blocked_combo_list` (mig 6300), BUKAN `preorder_blocked_combos`
 * (mig 5830, dipensiunkan mig 6310): yang lama memulangkan satu BARIS per
 * kombinasi, dan PostgREST memotong respons berbentuk baris di `max-rows`
 * 1.000 — produk ber-1.079 kombinasi mati memajang 79 di antaranya sebagai
 * kombinasi yang dijual. Yang baru memulangkan SATU nilai jsonb, jadi
 * daftarnya selalu utuh.
 *
 * `storeId` null → RPC memakai `current_store_id()`, yang untuk klien anon ini
 * NULL → `[]` (aturan A2: toko tak ter-resolusi = tak ada yang dikunci, bukan
 * daftar lintas toko). Best-effort: gagal → daftar kosong = perilaku sebelum
 * mig 5830. Gerbang yang mengikat ada di `resolve_or_create_preorder_variant`.
 */
export async function fetchPublicBlockedCombos(
  productId: string,
  storeId: string | null
): Promise<string[][]> {
  if (!supabase || !productId) return [];
  try {
    const { data, error } = await withTimeout(
      supabase.rpc("preorder_blocked_combo_list", {
        p_product_id: productId,
        p_store_id: storeId,
      }),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (error) return [];
    return parseBlockedComboList(data);
  } catch {
    return [];
  }
}

/**
 * Kombinasi yang DIJUAL produk TERTUTUP (B1) — daftar-IZIN `option_ids` baris
 * aktif lewat RPC `preorder_sellable_combo_list` (satu jsonb, tak terpotong
 * max-rows; cermin `fetchPublicBlockedCombos`).
 *
 * Arah gagal BERBEDA dengan daftar mati: `null` (gagal dimuat) BUKAN "tak ada
 * yang dijual" — pemanggil jatuh ke daftar mati biasa, dan gerbang yang
 * mengikat tetap resolver (menolak kombinasi tanpa baris produk tertutup).
 * Toko tak ter-resolusi → RPC memulangkan `[]` (aturan A2).
 */
export async function fetchPublicSellableCombos(
  productId: string,
  storeId: string | null
): Promise<string[][] | null> {
  if (!supabase || !productId) return null;
  try {
    const { data, error } = await withTimeout(
      supabase.rpc("preorder_sellable_combo_list", {
        p_product_id: productId,
        p_store_id: storeId,
      }),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (error) return null;
    return parseBlockedComboList(data);
  } catch {
    return null;
  }
}

/**
 * Nama gudang produk untuk badge "Dikirim dari …" halaman produk toko
 * (Multigudang, mig 7260). `null` = saklar Multigudang toko mati, produk bukan
 * milik toko itu / nonaktif, atau gagal — badge tak dirender.
 *
 * Lewat RPC karena `products.warehouse_id` & tabel `warehouses` sengaja tertutup
 * bagi anon (mig 7200); RPC hanya memulangkan SATU nama untuk produk ini.
 * `storeId` WAJIB (aturan A2): toko tak ter-resolusi = tanpa badge, bukan
 * panggilan tanpa toko. JANGAN dipakai katalog reseller `/toko/[kode]` — nama
 * gudang toko induk tak boleh tampil di sana.
 */
export async function fetchPublicProductWarehouseName(
  productId: string,
  storeId: string | null
): Promise<string | null> {
  if (!supabase || !productId || !storeId) return null;
  try {
    const { data, error } = await withTimeout(
      supabase.rpc("public_product_warehouse_name", {
        p_product_id: productId,
        p_store_id: storeId,
      }),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (error || typeof data !== "string") return null;
    const name = data.trim();
    return name === "" ? null : name;
  } catch {
    return null;
  }
}

/**
 * Produk lengkap per slug untuk halaman produk, pesan Bagikan Jelajah, dan
 * modal Edit Pesanan pembeli.
 *
 * `storeId` WAJIB (aturan A2, bughunt G8-9): slug unik PER-TOKO (mig 3200),
 * bukan global — produk hasil clone lintas toko berbagi slug. Query ini lewat
 * klien anon, dan anon lolos cabang ESCAPE RLS sehingga melihat produk aktif
 * SEMUA toko. Tanpa filter toko, slug kembar memulangkan dua baris
 * (`maybeSingle` → PGRST116 → null), atau produk TOKO LAIN bila milik toko
 * sendiri nonaktif; sumbu harga (`product_has_price_axis(id, NULL)`) pun selalu
 * NULL sehingga harga sheet Edit Pesanan membantah harga yang ditulis Simpan.
 * Toko tak ter-resolusi = null tanpa menyentuh DB, bukan query tanpa filter.
 * Parameternya sengaja tidak opsional: pemanggil baru dipaksa memilih tokonya;
 * tripwire-nya `storeScopedQueries.test.ts`.
 */
export async function getPublicProductBySlug(
  slug: string,
  storeId: string | null,
  options?: {
    /**
     * Paksa muat SELURUH kombinasi varian, mematikan mode malas.
     *
     * Dipakai pemanggil yang benar-benar butuh daftar penuh dan tidak punya
     * jalur resolusi on-demand — saat ini HANYA `EditOrderModal`: di sana
     * pemilihan varian jatuh ke `fallbackVariations` yang ber-`optionIds: []`,
     * sehingga daftar kosong akan membuat `isVariantOptionSelectable`
     * menonaktifkan SEMUA opsi dan reseller tak bisa mengganti varian
     * pesanannya. Jalur itu jarang dibuka & sudah terautentikasi, jadi ia
     * sengaja mempertahankan perilaku lama alih-alih ikut dibuat malas
     * setengah jalan.
     */
    allVariants?: boolean;
  }
) {
  // FAIL-CLOSED (A2): toko tak ter-resolusi = "tidak ada produk", bukan
  // lookup slug lintas toko.
  if (!supabase || !storeId) {
    return null;
  }

  try {
    // Lookup slug per-toko: filter `store_id` SELALU ikut — slug kembar lintas
    // toko (clone) tak boleh menggagalkan `maybeSingle` atau menyajikan produk
    // toko lain di domain ini.
    const q = supabase
      .from("products")
      .select(PRODUCT_DETAIL_SELECT)
      .eq("slug", slug)
      .eq("store_id", storeId)
      // LAZY VARIANTS (B): batasi embed varian di SUMBERNYA. Tanpa ini, produk
      // preorder besar mengirim 4.644 baris — dan biayanya bukan cuma transfer:
      // RLS `product_variants` mengevaluasi ulang induknya SEKALI PER BARIS
      // (terukur `SubPlan ... loops=4644`). Membuang baris SESUDAH fetch tak
      // menolong sama sekali; pemotongannya harus terjadi di query.
      .limit(options?.allVariants ? PGREST_MAX_ROWS : VARIANT_FETCH_CAP, {
        foreignTable: "product_variants",
      });
    const { data, error } = await withTimeout(
      q.maybeSingle(),
      PUBLIC_FETCH_TIMEOUT_MS
    );

    if (error || !data) {
      return null;
    }

    // `as unknown` dulu: string select eksplisit yang panjang tak bisa diurai
    // parser tipe supabase-js (pola sama dgn cast di getPublicProductsByIds).
    const row = data as unknown as PublicProductRow;

    // Rentang harga varian SEBENARNYA (Fase 0, mig 5450). Menggantikan
    // ringkasan lama yang menyimpulkannya dari ≤201 baris yang sempat terambil
    // embed — dan embed itu tanpa `ORDER BY`, jadi baris mana
    // yang datang tak ditentukan query. Diukur di PROD: sampel `RONA NIRMALA`
    // memuat NOL baris berharga padahal produknya punya 10 (350rb–1.045rb),
    // sehingga halaman memajang Rp 350.000 sebagai harga pasti untuk 2.470
    // kombinasi. Kegagalan di sini FAIL-CLOSED (`priceUncertain`), bukan
    // "anggap seragam".
    const loadPriceRange = async (): Promise<{
      range: { min: number; max: number; minBase?: number; maxBase?: number } | null;
      uncertain: boolean;
    }> => {
      if (!supabase) return { range: null, uncertain: true };
      try {
        const { data, error } = await withTimeout(
          supabase.rpc("product_variant_price_range", {
            p_product_id: row.id,
            p_store_id: storeId ?? null,
          }),
          PUBLIC_FETCH_TIMEOUT_MS
        );
        if (error) return { range: null, uncertain: true };
        const first = (data as {
          min_price: number | null; max_price: number | null;
          min_total?: number | null; max_total?: number | null;
        }[] | null)?.[0];
        // mig 7900: rentang TOTAL (dasar + tambahan) — keseragaman dinilai atas
        // keduanya (`variantPriceSpread`). Sebelum 7950 terpasang kolomnya
        // absen → jatuh ke rentang dasar.
        const min = first?.min_total ?? first?.min_price ?? null;
        const max = first?.max_total ?? first?.max_price ?? null;
        // min/max NULL = produk ini memang belum punya harga varian sama sekali
        // (keadaan SAH: `products.price_normal` berlaku untuk semua kombinasi).
        // Itu BUKAN ketidaktahuan, jadi `uncertain` tetap false.
        if (min === null || max === null) return { range: null, uncertain: false };
        // MIN/MAX DASAR di samping rentang total — uji kesepakatan tambahan
        // varian tanpa harga sendiri (bughunt D14-2 / D15-20, `borrowedExtraMismatch`).
        const minBase = first?.min_total != null && first?.min_price != null ? first.min_price : undefined;
        const maxBase = first?.max_total != null && first?.max_price != null ? first.max_price : undefined;
        return {
          range:
            minBase === undefined
              ? { min, max }
              : maxBase === undefined
                ? { min, max, minBase }
                : { min, max, minBase, maxBase },
          uncertain: false
        };
      } catch {
        return { range: null, uncertain: true };
      }
    };

    /** Kombinasi yang DIMATIKAN admin — lihat `fetchPublicBlockedCombos`. */
    const loadBlockedCombos = (): Promise<string[][]> =>
      fetchPublicBlockedCombos(row.id, storeId ?? null);

    // Embed varian dibatasi `VARIANT_FETCH_CAP`. Menyentuh batas = masih ada
    // sisa, dan daftar SEPARUH berbahaya: picker akan mengunci opsi yang
    // sebenarnya ada (kombinasi jadi tak bisa dipesan — kelas bug mig 212).
    // Karena itu daftar tak-lengkap tak pernah dipakai apa adanya; ia harus
    // dilengkapi ATAU dibuang seluruhnya demi resolusi on-demand.
    const fetchedCount = row.product_variants?.length ?? 0;
    let variantsLazy = false;
    let priceRange: { min: number; max: number; minBase?: number; maxBase?: number } | null = null;
    let priceUncertain = false;
    let blockedCombos: string[][] = [];
    // Daftar-IZIN — hanya produk TERTUTUP > 200 (B1, `storefrontComboSource`).
    let sellableCombos: string[][] | null = null;
    const cap = options?.allVariants ? PGREST_MAX_ROWS : VARIANT_FETCH_CAP;

    // Dua tambahan mode malas selalu diambil BERBARENGAN: keduanya satu
    // round-trip untuk produk yang sama, dan menjalankannya berurutan akan
    // menambah latensi halaman yang paling sering dibuka se-aplikasi.
    // `allowList` (produk tertutup): daftar yang DIJUAL menggantikan daftar
    // mati; gagal dimuat → jatuh ke daftar mati (resolver tetap menolak).
    const loadLazyExtras = async (allowList = false): Promise<void> => {
      const [priced, blocked, sellable] = await Promise.all([
        loadPriceRange(),
        allowList ? Promise.resolve<string[][]>([]) : loadBlockedCombos(),
        allowList ? fetchPublicSellableCombos(row.id, storeId ?? null) : Promise.resolve(null),
      ]);
      priceRange = priced.range;
      priceUncertain = priced.uncertain;
      blockedCombos = blocked;
      sellableCombos = sellable;
      if (allowList && !sellable) blockedCombos = await loadBlockedCombos();
    };

    // BESARNYA PRODUK DIUKUR DARI POHON OPSI, bukan dari jumlah baris terambil.
    // Sejak materialisasi malas (A) kedua angka itu berbeda: produk 800
    // kombinasi yang semua kombinasinya masih inert punya NOL baris, sehingga
    // `fetchedCount` tak pernah menyentuh cap dan mode malas tak pernah menyala
    // — picker jadi kosong dan tombol pesan mati permanen (`canAdd` menuntut
    // `selectedVariation` ATAU `variantsLazy`; di sini dua-duanya kosong).
    // Memakai ambang & fungsi yang SAMA PERSIS dengan sisi tulis
    // (`shouldSkipInertVariantRows`) membuat "baris tak dibuat" dan "daftar tak
    // dikirim" kembali jadi satu keputusan, seperti yang dijanjikan komentarnya.
    const comboCount = deriveComboCount(
      (row.variant_groups ?? []).map((g) => g.variant_options?.length ?? 0)
    );

    // SUMBU HARGA (mig 7680) — dimulai di sini supaya berjalan PARALEL dengan
    // pelengkapan varian / ringkasan mode malas di bawah, lalu ditunggu
    // sebelum pemetaan. Gagal → null → pembaca harga fail-closed.
    const priceAxisPromise = fetchPublicProductHasPriceAxis(row.id, storeId ?? null);
    // SATU pintu (B1): tanpa tanda mode = `shouldSkipInertVariantRows` persis;
    // produk TERTUTUP > 200 = malas dengan daftar-IZIN.
    const comboSource = storefrontComboSource({
      productType: row.product_type ?? "preorder",
      poLimited: row.po_limited,
      absentCombosSellable: row.absent_combos_sellable,
      comboCount,
      fetchedCount,
    });
    if (!options?.allVariants && comboSource !== "rows") {
      // Daftar yang ada (0 baris, atau SEBAGIAN kombinasi yang sudah membawa
      // harga/stok) sengaja dibuang: dipakai apa adanya ia akan mengunci
      // kombinasi yang sebenarnya sah — kelas bug mig 212. Semuanya diresolusi
      // on-demand lewat `option_ids`, dan barisnya dibuat saat ditambahkan ke
      // keranjang.
      variantsLazy = true;
      await loadLazyExtras(comboSource === "lazy-allow");
      row.product_variants = [];
    } else if (fetchedCount >= cap) {
      if (
        !options?.allVariants &&
        shouldLazyLoadVariants({
          productType: row.product_type ?? "preorder",
          fetchedCount,
          cap,
        })
      ) {
        // PREORDER besar → mode malas: buang daftarnya, picker dirender dari
        // pohon opsi, satu kombinasi diresolusi saat dipilih.
        variantsLazy = true;
        await loadLazyExtras();
        row.product_variants = [];
      } else if (supabase) {
        // READY STOCK > ambang → WAJIB lengkap: grey-out opsi ditentukan stok
        // agregat lintas kombinasi, mustahil benar dari daftar separuh. (Per
        // 2026-08 tak ada produk ready stock >200 varian di PROD — jalur ini
        // pengaman untuk yang dibuat kemudian.)
        try {
          row.product_variants = await withTimeout(
            fetchAllProductVariants<
              NonNullable<PublicProductRow["product_variants"]>[number]
            >(supabase, row.id, PUBLIC_VARIANT_COLUMNS),
            VARIANT_REFETCH_TIMEOUT_MS
          );
        } catch {
          // Gagal melengkapi → jatuh ke mode malas alih-alih menyajikan daftar
          // terpotong. Harga/stok jadi butuh satu resolusi tambahan, tapi SEMUA
          // kombinasi tetap bisa dipesan; daftar terpotong tidak.
          variantsLazy = true;
          await loadLazyExtras();
          row.product_variants = [];
        }
      }
    }

    const priceAxis = await priceAxisPromise;
    const mapped = { ...mapPublicProduct(row), priceAxis };
    return variantsLazy
      ? {
          ...mapped,
          variantsLazy: true,
          variantPriceRange: priceRange,
          variantPriceUncertain: priceUncertain,
          blockedOptionCombos: blockedCombos,
          ...(sellableCombos ? { sellableOptionCombos: sellableCombos } : {}),
        }
      : mapped;
  } catch {
    // Timeout/jaringan → perlakukan seperti "tak ditemukan" (null) agar SSR
    // tetap membalas cepat alih-alih menggantung.
    return null;
  }
}

export type PublicNamedItem = { id: string; name: string; iconUrl?: string };

/**
 * Master kategori/merek untuk beranda (tile Kategori + chip Merek). Toleran
 * error → [] supaya beranda tak pernah gagal render; HomeClient punya fallback
 * derive dari nilai unik products bila list kosong.
 *
 * `icon_url` (mig 0951) IKUT ditarik: ikon kategori yang diunggah admin dipakai
 * tile beranda & kartu landing `/kategori`. Tanpa kolom ini di select, ikon
 * unggahan tak pernah sampai ke storefront dan semua kategori tampil dengan
 * ikon lucide bawaan — persis bentuk `getPublicBrands` di bawah.
 */
export async function getPublicCategories(
  storeId?: string | null
): Promise<PublicNamedItem[]> {
  if (!supabase) {
    return [];
  }

  let q = supabase
    .from("product_categories")
    .select("id, name, icon_url")
    .eq("is_active", true);
  if (storeId) q = q.eq("store_id", storeId); // FASE 4: master per-toko
  const { data, error } = await q.order("name", { ascending: true });

  if (error || !data) {
    return [];
  }

  return (data as Array<{ id: string; name: string; icon_url: string | null }>).map(
    (c) => ({ id: c.id, name: c.name, iconUrl: c.icon_url ?? undefined })
  );
}

export async function getPublicBrands(
  storeId?: string | null
): Promise<PublicNamedItem[]> {
  if (!supabase) {
    return [];
  }

  let q = supabase
    .from("product_brands")
    .select("id, name, icon_url")
    .eq("is_active", true);
  if (storeId) q = q.eq("store_id", storeId); // FASE 4: master per-toko
  const { data, error } = await q.order("name", { ascending: true });

  if (error || !data) {
    return [];
  }

  return (data as Array<{ id: string; name: string; icon_url: string | null }>).map(
    (b) => ({ id: b.id, name: b.name, iconUrl: b.icon_url ?? undefined })
  );
}

/** Hiasan tingkat-MEREK untuk halaman detail produk. Keduanya opsional. */
export type PublicBrandBadge = {
  /** `product_brands.telegram_url` (mig 3440) → kanonik `https://t.me/…`. */
  telegramUrl: string | null;
  /** `product_brands.icon_url` (mig 096) → logo di badge merek. */
  iconUrl: string | null;
};

const EMPTY_BRAND_BADGE: PublicBrandBadge = { telegramUrl: null, iconUrl: null };

/**
 * Hiasan MEREK sebuah produk untuk halaman detail produk: link Telegram
 * (tombol "Materi iklan") + logo merek (badge merek). Satu query untuk
 * KEDUANYA — sumbernya satu baris `product_brands` yang sama, jadi memisahnya
 * cuma menambah roundtrip pada jalur yang sama.
 *
 * ── Kenapa cocok BY NAMA, bukan join ────────────────────────────────────────
 * `products.brand` adalah SNAPSHOT TEKS, bukan FK (lihat mig 3440) — tak ada
 * relasi yang bisa di-embed PostgREST. Pencocokannya `trim().toLowerCase()` DI
 * JS, bukan `.eq("name", …)`: mig 0090 menyemai master dari
 * `distinct trim(brand)` produk, jadi drift spasi/kapital pada baris lama
 * memang mungkin ada dan `.eq` akan diam-diam meleset.
 *
 * ── Store-scoping (aturan A2) ───────────────────────────────────────────────
 * Jalur ini dicapai TANPA LOGIN, dan sesi anon lolos cabang ESCAPE RLS
 * (`current_store_id()` NULL) sehingga melihat merek SEMUA toko. Karena itu
 * `storeId` kosong = FAIL-CLOSED (keduanya `null`, tanpa menyentuh DB), dan
 * query selalu membawa `.eq("store_id", storeId)`.
 *
 * Query-nya sengaja hanya menarik merek yang BENAR-BENAR membawa salah satu
 * hiasan itu — biasanya segelintir baris tiga kolom, dan toko yang belum
 * memakai keduanya membayar payload nyaris nol.
 *
 * Setiap mode gagal (timeout, kolom belum ada karena migrasi tertinggal, merek
 * tak ketemu, nilai tersimpan tak sah) memulangkan `null` per-medan → tombol
 * dan/atau logo hilang. Halaman produk TIDAK pernah ikut jatuh hanya karena
 * hiasan.
 */
export async function getPublicBrandBadge(
  brandName: string | null | undefined,
  storeId?: string | null
): Promise<PublicBrandBadge> {
  const name = (brandName ?? "").trim().toLowerCase();
  if (!supabase || !name || !storeId) {
    return EMPTY_BRAND_BADGE;
  }

  try {
    const { data, error } = await withTimeout(
      supabase
        .from("product_brands")
        .select("name, telegram_url, icon_url")
        .eq("store_id", storeId)
        .eq("is_active", true)
        .or("telegram_url.not.is.null,icon_url.not.is.null"),
      PUBLIC_FETCH_TIMEOUT_MS
    );

    if (error || !data) {
      return EMPTY_BRAND_BADGE;
    }

    const rows = data as Array<{
      name: string | null;
      telegram_url: string | null;
      icon_url: string | null;
    }>;
    const hit = rows.find((b) => (b.name ?? "").trim().toLowerCase() === name);
    // Normalisasi ULANG saat baca: form admin sudah menyimpan bentuk kanonik,
    // tapi baris pra-mig 3440 / hasil edit manual di DB tidak dijamin. Nilai
    // tak dikenali → null → tombol/logo disembunyikan, bukan tautan & gambar
    // rusak.
    return {
      telegramUrl: normalizeBrandTelegramUrl(hit?.telegram_url ?? null),
      iconUrl: normalizeBrandIconUrl(hit?.icon_url ?? null),
    };
  } catch {
    return EMPTY_BRAND_BADGE;
  }
}

/**
 * Master segmen (mig 159) untuk drill-down kategori beranda (ketuk Gamis → pilih
 * Anak/Dewasa) + faset /cari. Toleran error → [] supaya beranda tak pernah gagal.
 */
export async function getPublicSegments(
  storeId?: string | null
): Promise<PublicNamedItem[]> {
  if (!supabase) {
    return [];
  }

  let q = supabase
    .from("product_segments")
    .select("id, name")
    .eq("is_active", true);
  if (storeId) q = q.eq("store_id", storeId); // FASE 4: master per-toko
  const { data, error } = await q.order("name", { ascending: true });

  if (error || !data) {
    return [];
  }

  return data as PublicNamedItem[];
}

/**
 * Resolusi SATU kombinasi varian dari opsi yang dipilih pembeli — jalur
 * pengganti "kirim semua varian" untuk produk mode malas (lihat
 * `src/lib/lazyVariants.ts`).
 *
 * Kuncinya `option_ids`, BUKAN nama: itu identitas sebenarnya (terbukti unik
 * untuk seluruh 49.471 baris ber-opsi di PROD), sedangkan `name` label turunan
 * yang bisa desync — 1 baris di PROD sudah menyimpang dari nama turunannya, dan
 * mencocokkan nama akan membuat kombinasi itu "hilang" senyap bagi pembeli.
 *
 * `contains` (`@>`) dipasangkan `product_id` DENGAN SENGAJA: diukur di PROD,
 * lewat index `product_id` = 52 ms sedangkan `option_ids` sendirian (index GIN)
 * justru 139 ms — jadi tak ada index baru yang perlu dikirim untuk ini.
 *
 * SCOPE TOKO — kenapa ini TIDAK fail-closed seperti `fetchActivePromoCampaigns`:
 * di sana pencocokan target kampanye dilakukan BY NAMA, sehingga toko tak
 * ter-resolusi benar-benar bisa menyeret data toko lain. Di sini kuncinya
 * `product_id` dari produk yang SUDAH diresolusi ter-scope toko oleh
 * pemanggilnya — varian milik produk X menurut definisi berada di toko X, jadi
 * scope-nya terbawa transitif. `store_id` tetap dikirim sebagai lapis kedua
 * bila diketahui, tapi memaksanya fail-closed hanya akan mematikan pemilihan
 * varian di halaman yang produknya sendiri sudah tampil (mis. host lokal).
 *
 * `@>` berarti "memuat", bukan "sama persis". Selama pilihan belum lengkap
 * (belum satu opsi per grup) ia bisa cocok banyak baris; pemanggil karena itu
 * WAJIB hanya memanggil saat `isSelectionComplete`. Sebagai jaring kedua,
 * fungsi ini menolak hasil ganda (`limit 2`) alih-alih diam-diam memilih yang
 * pertama — memilih sembarang kombinasi berarti pembeli memesan barang yang
 * tidak ia pilih.
 */
export async function resolvePublicVariant(
  productId: string,
  optionIds: string[],
  storeId?: string | null
): Promise<Product["variations"][number] | null> {
  if (!supabase || !productId || optionIds.length === 0) return null;

  try {
    let q = supabase
      .from("product_variants")
      .select(PUBLIC_VARIANT_COLUMNS)
      .eq("product_id", productId)
      .contains("option_ids", optionIds)
      .limit(2);
    if (storeId) q = q.eq("store_id", storeId);

    const { data, error } = await withTimeout(q, PUBLIC_FETCH_TIMEOUT_MS);
    if (error || !data || data.length !== 1) return null;

    const [variant] = data as unknown as NonNullable<
      PublicProductRow["product_variants"]
    >;
    return mapPublicVariant(variant);
  } catch {
    // Timeout/jaringan → null. Pemanggil menahan tombol pesan (harga belum
    // pasti); menebak harga jauh lebih buruk daripada meminta pembeli mencoba
    // lagi.
    return null;
  }
}

/**
 * Baris varian ber-harga SENDIRI sebuah produk MODE MALAS — bahan JSON-LD
 * halaman produk (PR-IG-3). Halaman malas tak memegang daftar variannya
 * (`variations: []`), padahal JSON-LD wajib menyebut harga PER VARIAN: harga
 * produknya di mode ini hanya MIN seluruh varian (akar B3).
 *
 * Himpunannya = item feed Katalog Instagram produk malas: CTE `pilihan`
 * (mig 6530) mendahulukan baris berharga dalam urutan admin lalu memotong di
 * 100, dan baris tanpa harga dilewati feed (`kombinasi_tanpa_harga`). Jadi:
 * aktif (RLS pembeli), `price_normal` terisi, `sort_order` lalu `id`, ≤`limit`.
 *
 * FAIL-CLOSED (aturan A2): toko tak ter-resolusi / gagal → `null`, bukan
 * daftar lintas toko dan bukan `[]` — `[]` berarti "memang tak ada baris
 * berharga", keadaan sah yang membuat harga produk berlaku seragam.
 */
export async function getPublicPricedVariants(
  productId: string,
  storeId: string | null,
  limit: number
): Promise<Product["variations"] | null> {
  if (!supabase || !productId) return null;
  if (!storeId) return null;
  try {
    const { data, error } = await withTimeout(
      supabase
        .from("product_variants")
        .select(PUBLIC_VARIANT_COLUMNS)
        .eq("product_id", productId)
        .eq("store_id", storeId)
        .not("price_normal", "is", null)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true })
        .limit(limit),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (error || !Array.isArray(data)) return null;
    return (data as unknown as NonNullable<PublicProductRow["product_variants"]>).map(
      mapPublicVariant
    );
  } catch {
    return null;
  }
}

/**
 * URL CHECKOUT META (`/keranjang/meta`): id item katalog → baris TOKO INI.
 * Id-nya UUID VARIAN (feed Katalog Instagram) atau UUID PRODUK (katalog CSV
 * lama), dan URL-nya bisa dirakit siapa saja — maka dua query ber-`store_id`:
 *   1. varian ber-id itu (kolom publik + `product_id`);
 *   2. `id, slug` produk: induk varian di atas + id yang bukan varian.
 * Varian/produk nonaktif tak terbaca RLS pembeli → absen = tidak ditemukan.
 *
 * FAIL-CLOSED (aturan A2): toko tak ter-resolusi / gagal / timeout → `null`
 * (halaman menyebut "gagal memuat"), bukan baris lintas toko dan bukan peta
 * kosong — peta kosong berarti "memang tak ada di toko ini".
 */
export async function getMetaCheckoutRefs(
  ids: readonly string[],
  storeId: string | null
): Promise<MetaCheckoutRefs | null> {
  if (!supabase) return null;
  if (!storeId) return null;
  const variants = new Map<string, { productId: string; variation: Product["variations"][number] }>();
  const productSlugs = new Map<string, string>();
  if (ids.length === 0) return { variants, productSlugs };
  try {
    const { data: variantRows, error: variantError } = await withTimeout(
      supabase
        .from("product_variants")
        .select(`product_id, ${PUBLIC_VARIANT_COLUMNS}`)
        .in("id", [...ids])
        .eq("store_id", storeId),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (variantError || !Array.isArray(variantRows)) return null;
    type Row = NonNullable<PublicProductRow["product_variants"]>[number] & { product_id: string };
    for (const row of variantRows as unknown as Row[]) {
      variants.set(row.id, { productId: row.product_id, variation: mapPublicVariant(row) });
    }

    const productIds = [
      ...new Set([
        ...[...variants.values()].map((v) => v.productId),
        ...ids.filter((id) => !variants.has(id)),
      ]),
    ];
    const { data: productRows, error: productError } = await withTimeout(
      supabase.from("products").select("id, slug").in("id", productIds).eq("store_id", storeId),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (productError || !Array.isArray(productRows)) return null;
    for (const row of productRows as { id: string; slug: string }[]) {
      productSlugs.set(row.id, row.slug);
    }
    return { variants, productSlugs };
  } catch {
    return null;
  }
}

/**
 * HARGA SLOT untuk satu kombinasi, TANPA memateralisasi barisnya (FASE 4,
 * mig 5510).
 *
 * MASALAH YANG DISELESAIKAN. Materialisasi sengaja hanya terjadi saat menambah
 * ke keranjang (`preorderVariant.ts`) — membuat baris karena pembeli sekadar
 * menjelajah kombinasi akan memateralisasi kartesius lewat pintu belakang.
 * Akibatnya di mode malas kombinasi yang belum pernah dipesan tak punya baris,
 * `resolvePublicVariant` memulangkan null, dan layar jatuh ke varian
 * PERWAKILAN lalu menahan angkanya sebagai "harga menyusul" — padahal sejak
 * mig 5500 harga kombinasi itu SUDAH pasti: ia yang akan ditulis begitu tombol
 * pesan ditekan.
 *
 * ATURANNYA SATU, PEMAKAINYA DUA. RPC ini fungsi yang PERSIS SAMA dengan yang
 * dipanggil `resolve_or_create_preorder_variant` saat menulis barisnya
 * (`preorder_inherited_pricing`, mig 5510). Menyalin rumusnya ke sini akan membuat
 * layar dan nota bisa MELENCENG — bentuk kegagalan natasyashop #2095. Karena
 * itu jangan pernah menghitung harga slot di TypeScript.
 *
 * GAGAL = `null` = "kami tidak tahu", dan pemanggil memperlakukannya seperti
 * sekarang (harga menyusul). Diam-diam menebak jauh lebih buruk.
 */
export type InheritedPricing = {
  /** `null` = gerbang slot tidak terpenuhi → tetap "harga menyusul". */
  priceNormal: number | null;
  diskonMode?: "persen" | "nominal";
  diskonReseller?: number;
  diskonAgen?: number;
  diskonSlot3?: number;
  diskonSlot4?: number;
  diskonSlot5?: number;
  diskonSlot6?: number;
  diskonSlot7?: number;
  diskonSlot8?: number;
  diskonSlot9?: number;
  diskonSlot10?: number;
  diskonSlot11?: number;
  diskonSlot12?: number;
  diskonSlot13?: number;
  diskonSlot14?: number;
  diskonSlot15?: number;
  /** Tambahan yang diwarisi (mig 7900) — null/absen = 0. */
  priceExtra?: number | null;
};

/**
 * SUMBU HARGA (2026-10-20, mig 7680) — produk ini menyatakan grup penentu harga
 * (`products.price_affecting_groups` tak kosong)? Dibaca lewat RPC karena
 * kolomnya sengaja tak ter-grant ke anon (daftar sensitif mig 4280). `true` →
 * varian tanpa harga sendiri hanya boleh berharga dari warisan slot; `false` →
 * aturan lama; `null` = tak diketahui (RPC gagal / produk toko lain) →
 * `resolveSelectionPrice` memperlakukannya fail-closed.
 */
export async function fetchPublicProductHasPriceAxis(
  productId: string,
  storeId?: string | null
): Promise<boolean | null> {
  return fetchPublicProductHasPriceAxisStrict(productId, storeId).catch(() => null);
}

/**
 * Kembar KETAT `fetchPublicProductHasPriceAxis`: gagal / tak diketahui =
 * LEMPAR. Untuk jalur yang MENULIS ULANG harga yang sudah ada (Simpan Edit
 * Pesanan, tinjauan PR E): "sumbu tak diketahui" yang di checkout berarti
 * harga menyusul untuk baris BARU, di sana berarti `unitPrice: null` di atas
 * harga tersimpan — jadi konteksnya harus gagal utuh (snapshot lama dipakai).
 */
export async function fetchPublicProductHasPriceAxisStrict(
  productId: string,
  storeId?: string | null
): Promise<boolean> {
  if (!supabase || !productId) throw new Error("Sumbu harga produk tidak bisa dimuat");
  const { data, error } = await withTimeout(
    supabase.rpc("product_has_price_axis", {
      p_product_id: productId,
      p_store_id: storeId ?? null,
    }),
    PUBLIC_FETCH_TIMEOUT_MS
  );
  if (error) throw error;
  if (typeof data !== "boolean") throw new Error("Sumbu harga produk tidak diketahui");
  return data;
}

export async function resolvePublicInheritedPricing(
  productId: string,
  optionIds: string[],
  storeId?: string | null
): Promise<InheritedPricing | null> {
  return resolvePublicInheritedPricingStrict(productId, optionIds, storeId).catch(() => null);
}

/**
 * Kembar KETAT `resolvePublicInheritedPricing`: `null` HANYA berarti "tak ada
 * warisan" (saudara belum berharga / tak sepakat); gagal memuat = LEMPAR.
 * Alasannya sama dengan `fetchPublicProductHasPriceAxisStrict` (tinjauan PR E).
 */
export async function resolvePublicInheritedPricingStrict(
  productId: string,
  optionIds: string[],
  storeId?: string | null
): Promise<InheritedPricing | null> {
  if (!productId || optionIds.length === 0) return null;
  if (!supabase) throw new Error("Warisan harga tidak bisa dimuat");
  const { data, error } = await withTimeout(
    supabase.rpc("preorder_inherited_pricing", {
      p_product_id: productId,
      p_store_id: storeId ?? null,
      p_option_ids: optionIds,
    }),
    PUBLIC_FETCH_TIMEOUT_MS
  );
  if (error) throw error;
  return inheritedPricingFromRows(data as InheritedPricingRpcRow[] | null);
}

// MATERIALISASI SAAT DIPESAN (A) — `resolveOrCreatePreorderVariant` kini
// hidup di `preorderVariant.ts`, BUKAN di sini. Modul ini memakai klien anon
// tanpa sesi (`@/lib/supabase/client`); RPC itu menulis dan EXECUTE-nya hanya
// untuk `authenticated`, jadi dari sini ia selalu ditolak 42501 — bug live
// 2026-09-02 (ZURIAH by MOSLEM DAILY). Jangan dipindahkan kembali ke sini.

/**
 * Pcs yang SUDAH dipesan per varian untuk produk PO berkuota (RPC
 * `get_preorder_quota_remaining`, mig 3970).
 *
 * Lewat RPC, bukan query langsung: `order_items` tertutup RLS bagi pembeli lain
 * dan anon, jadi agregatnya mustahil dihitung dari klien. Yang dipulangkan hanya
 * angka — tak pernah baris order milik siapa pun.
 *
 * DIPANGGIL HANYA bila `product.poLimited`. Produk PO biasa (mayoritas) tidak
 * membayar round-trip apa pun; itu sekalian alasan toggle-nya ada di produk,
 * bukan disimpulkan dari kolom kuota varian yang harus dibaca dulu.
 *
 * Gagal/timeout → peta KOSONG, yang berarti "belum ada yang memesan" alias
 * paling permisif. Itu disengaja: gerbang otoritatifnya trigger DB
 * `enforce_preorder_quota`, jadi peta yang meleset paling jauh membuat pembeli
 * menerima penolakan saat menambah ke keranjang — jauh lebih baik daripada
 * mengunci produk yang sebenarnya masih terbuka karena jaringan sedang buruk.
 */
export type PreorderQuotaRow = {
  variantId: string;
  quota: number | null;
  used: number;
};

/**
 * Baris kuota LENGKAP (kuota + terpakai) per varian — dipakai layar ADMIN yang
 * perlu menampilkan "terpakai / kuota". Storefront cukup `fetchPreorderQuotaUsed`
 * di bawahnya, yang membuang kolom kuota karena angkanya sudah ikut baris varian.
 */
export async function fetchPreorderQuotaRows(
  productId: string
): Promise<PreorderQuotaRow[]> {
  if (!supabase || !productId) return [];
  try {
    const { data, error } = await withTimeout(
      supabase.rpc("get_preorder_quota_remaining", { p_product_id: productId }),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (error || !Array.isArray(data)) return [];
    return (data as Array<{ variant_id: string; quota: number | null; used: number | null }>)
      .filter((r) => Boolean(r?.variant_id))
      .map((r) => ({
        variantId: r.variant_id,
        quota: r.quota ?? null,
        used: Number(r.used ?? 0),
      }));
  } catch {
    return [];
  }
}

export async function fetchPreorderQuotaUsed(
  productId: string
): Promise<Record<string, number>> {
  if (!supabase || !productId) return {};
  try {
    const { data, error } = await withTimeout(
      supabase.rpc("get_preorder_quota_remaining", { p_product_id: productId }),
      PUBLIC_FETCH_TIMEOUT_MS
    );
    if (error || !Array.isArray(data)) return {};
    const map: Record<string, number> = {};
    for (const row of data as Array<{ variant_id: string; used: number | null }>) {
      if (row?.variant_id) map[row.variant_id] = Number(row.used ?? 0);
    }
    return map;
  } catch {
    return {};
  }
}

// Kolom untuk PEMBACAAN ULANG stok saat tombol "Bagikan ke WA" ditekan.
// SENGAJA terpisah dari `PUBLIC_VARIANT_COLUMNS`: yang itu memberi makan
// seluruh halaman produk, sementara di sini cuma tiga angka yang dibutuhkan
// pesan WhatsApp — dan produk PROD terbesar punya ribuan varian yang harus
// muat dalam anggaran waktu satu ketukan tombol.
const STOCK_SNAPSHOT_COLUMNS = "id, stock, po_quota";

// Anggaran waktu pembacaan ulang itu. Pendek DENGAN SENGAJA: ini berjalan di
// dalam gestur klik, dan kegagalannya punya jalan mundur yang benar (angka
// yang sudah di layar, dicap jam halaman memuatnya) — bukan pesan yang batal.
const STOCK_SNAPSHOT_TIMEOUT_MS = 5000;

type StockSnapshotRow = {
  id: string;
  stock: number | null;
  po_quota: number | null;
};

/**
 * Potret stok & kuota SELURUH varian sebuah produk, dibaca SEKARANG.
 *
 * Kenapa ada: pesan "Bagikan ke WA" kini mencantumkan tanggal & jam, dan
 * halaman produk cuma membaca stok SEKALI saat dimuat. Halaman yang dibiarkan
 * terbuka dua jam lalu dibagikan akan mengirim angka basi dua jam ber-cap jam
 * sekarang. Pembacaan ulang inilah yang membuat cap waktunya jujur.
 *
 * `null` = pembacaan GAGAL (jaringan/timeout/RPC menolak), dan itu SATU sinyal
 * untuk stok maupun kuota. Pemanggil wajib membedakannya dari "potret kosong":
 * yang pertama berarti "pakai angka lama + cap jam lama", yang kedua berarti
 * "produk ini memang tak punya varian".
 *
 * `withQuota` menyalakan RPC sisa kuota (`get_preorder_quota_remaining`) —
 * hanya produk PO berkuota yang membayar round-trip itu, cermin gerbang
 * `poLimited` di halaman produk.
 */
export type ProductStockSnapshot = {
  /** Stok fisik per variant id (produk ready stock). */
  stock: Record<string, number>;
  /** `po_quota` per variant id — `null` = varian tanpa jatah (tak terbatas). */
  quota: Record<string, number | null>;
  /** Pcs PO yang sudah dipesan per variant id. Kosong bila `withQuota` mati. */
  quotaUsed: Record<string, number>;
};

export async function fetchProductStockSnapshot(
  productId: string,
  options?: { withQuota?: boolean }
): Promise<ProductStockSnapshot | null> {
  const db = supabase;
  if (!db || !productId) return null;
  const withQuota = options?.withQuota ?? false;
  try {
    const [rows, quotaResult] = await Promise.all([
      withTimeout(
        fetchAllProductVariants<StockSnapshotRow>(
          db,
          productId,
          STOCK_SNAPSHOT_COLUMNS
        ),
        STOCK_SNAPSHOT_TIMEOUT_MS
      ),
      withQuota
        ? withTimeout(
            db.rpc("get_preorder_quota_remaining", { p_product_id: productId }),
            STOCK_SNAPSHOT_TIMEOUT_MS
          )
        : Promise.resolve(null),
    ]);

    const stock: Record<string, number> = {};
    const quota: Record<string, number | null> = {};
    for (const row of rows) {
      if (!row?.id) continue;
      stock[row.id] = Math.max(0, Math.floor(Number(row.stock ?? 0)));
      quota[row.id] = row.po_quota == null ? null : Number(row.po_quota);
    }

    const quotaUsed: Record<string, number> = {};
    if (withQuota) {
      // `error` WAJIB dibaca, tak boleh ditelan jadi peta kosong: peta kosong
      // berarti "nol pcs terpakai" = sisa kuota dilaporkan PENUH. Pada pesan
      // ber-cap waktu itu bukan fail-open biasa, itu angka karangan yang
      // dikirim ke pembeli. Gagal = seluruh potret gagal.
      if (!quotaResult || quotaResult.error || !Array.isArray(quotaResult.data)) {
        return null;
      }
      for (const row of quotaResult.data as Array<{
        variant_id: string;
        used: number | null;
      }>) {
        if (row?.variant_id) quotaUsed[row.variant_id] = Number(row.used ?? 0);
      }
    }

    return { stock, quota, quotaUsed };
  } catch {
    return null;
  }
}
